import React, { PureComponent, Fragment } from 'react';
import PropTypes, { instanceOf } from 'prop-types';
import { formShape, createForm } from 'rc-form';
import styled from 'styled-components';
import TosLinks from '../Account/TosLinks';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import { isEmptyObject } from '../../core/util/object';
import { analyticsTrackFormErrors, analyticsTrackEvent } from '../../core/analytics';
import { tenDigitPhoneValueToFormattedDisplay } from '../../core/util/phoneNumber';
import { OTP_LOGIN_FIELD_FOCUS, OTP_LOGIN_SUBMIT_ERROR } from '../../core/analytics/events';
import Loading from '../../components/SolvPatternLibrary/Loading';
import { otpCodeInputValidator } from '../../core/util/rcForm/validators';
import { colors } from '../../constants/colors';
import { fontSize, fontFamily } from '../../constants/text';
import TextField from '../SolvPatternLibrary/TextField';
import PersistedLoginInput from '../SignUpForm/Fields/PersistedLoginInput';
import { withCookies, Cookies } from 'react-cookie';
import { redirectAfterLogin, shouldFinishSignup } from '../../core/login';
import withAuthentication from '../../core/login/withAuthentication';

const PHONE_FIELD_NAME = 'phone';
const CODE_FIELD_NAME = 'code';
const TOS_ACCEPTED_FIELD_NAME = 'tosAccepted';

const Title = styled.h3`
  color: ${colors.blueLoadingScreen};
  font-size: ${fontSize.medium};
  font-family: ${fontFamily.bold};
  letter-spacing: -0.03px;
  line-height: 22px;
  margin-bottom: 5px;
`;

const Subtitle = styled.div`
  color: ${colors.lightText};
  font-size: ${fontSize.default};
`;

const Form = styled.form`
  width: 100%;
`;

const CTA = styled(Button)`
  margin-top: 10px;
  padding: 0;
  height: 52px;
`;

const SecondaryCTA = styled.a`
  display: inline-flex;
  font-size: ${fontSize.default};
  margin: 0;
`;

const TosMessage = styled.div`
  font-size: ${fontSize.mediumSmall};
  color: ${colors.darkText};
  margin-top: 18px;
`;

class OtpCodeLoginForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      errors: {},
      submitted: false,
      submitting: false,
      submitButtonDisabled: true,
      isRedirecting: false,
    };
  }

  onFieldChange = (value, fieldName) => {
    const { form } = this.props;
    this.setState({ errors: { [fieldName]: false } });
    form.setFieldsValue({ [fieldName]: value });
    form.validateFields(this.enableFormSubmissionIfNoErrors);
  };

  onBlur = fieldName => {
    const { form } = this.props;
    const currentErrors = this.state.errors;
    form.validateFields([fieldName], {}, errors => {
      this.enableFormSubmissionIfNoErrors(errors);
      if (isEmptyObject(errors)) {
        this.setState({ errors: { ...currentErrors, [fieldName]: false } });
      } else {
        this.setState({
          errors: { ...currentErrors, [fieldName]: errors[fieldName] },
        });
      }
    });
  };

  onFocus = () => analyticsTrackEvent(OTP_LOGIN_FIELD_FOCUS);

  onSubmit = e => {
    e && e.preventDefault();

    const { form, handleOtpFormSubmit, cookies } = this.props;

    this.setState({ submitting: true });
    form.validateFields((errors, values) => {
      if (isEmptyObject(errors)) {
        return handleOtpFormSubmit(values, cookies, this.onSubmitError);
      }

      this.setState({ submitting: false });
      analyticsTrackFormErrors(OTP_LOGIN_SUBMIT_ERROR, errors);
      return null;
    });
  };

  onSubmitError = error =>
    this.setState({ submitting: false, errors: { [CODE_FIELD_NAME]: error } });

  getOtpCodeFieldHelperText = () => {
    const { login } = this.props;
    const { errors } = this.state;
    if (!isEmptyObject(errors[CODE_FIELD_NAME])) {
      if (login.accountError) {
        return 'Please double check the PIN and try again';
      } else if (login.maxFailLimit) {
        return "Your code expired. That's okay, you can request a new one.";
      }
    }

    return '';
  };

  getErrorState = () => {
    const { errors } = this.state;
    return Boolean(errors[CODE_FIELD_NAME]);
  };

  enableFormSubmissionIfNoErrors = errors =>
    isEmptyObject(errors)
      ? this.state.submitButtonDisabled && this.setState({ submitButtonDisabled: false })
      : this.state.submitButtonDisabled || this.setState({ submitButtonDisabled: true });

  handleResendCode = () => this.props.handleFormSubmit(this.props.form.getFieldsValue());

  isSendCodeButtonDisabled = () => {
    const { submitting, submitButtonDisabled } = this.state;

    return submitButtonDisabled || submitting;
  };

  redirectAfterAccountReceived = () => {
    const {
      preventRedirectAfterLogin,
      isBookingWidgetEmbedded,
      accountSummary,
      redirectToUrl,
      goToSignUp,
      isLoggedIn,
    } = this.props;

    if (shouldFinishSignup(accountSummary)) {
      goToSignUp();
      return;
    }

    if (preventRedirectAfterLogin) return;

    if (isLoggedIn) {
      this.setState({ isRedirecting: true }, () =>
        redirectAfterLogin(accountSummary, redirectToUrl, isBookingWidgetEmbedded)
      );
    }
  };

  render() {
    const { isRedirecting } = this.state;
    const { accountSummary, form, initialValues } = this.props;
    const phoneFieldCurrentValue =
      form.getFieldProps(PHONE_FIELD_NAME) && form.getFieldProps(PHONE_FIELD_NAME).value;
    const phoneValue = phoneFieldCurrentValue || initialValues.phone;
    if (!isRedirecting && !isEmptyObject(accountSummary)) {
      this.redirectAfterAccountReceived();

      return <Loading />;
    }

    if (isRedirecting) {
      return <Loading />;
    }

    return (
      <Fragment>
        <Title>Enter verification code</Title>
        {phoneValue && (
          <Subtitle>
            We texted your verification code to
            <br />
            {tenDigitPhoneValueToFormattedDisplay(phoneValue)}.{' '}
            <SecondaryCTA onClick={this.handleResendCode}>Resend code</SecondaryCTA>
          </Subtitle>
        )}
        <Form onSubmit={this.onSubmit}>
          <input
            {...form.getFieldProps(PHONE_FIELD_NAME, {
              initialValue: phoneValue,
            })}
            type="hidden"
          />
          <TextField
            {...form.getFieldProps(CODE_FIELD_NAME, {
              rules: [
                {
                  required: true,
                  validator: otpCodeInputValidator,
                },
              ],
              initialValue: initialValues[CODE_FIELD_NAME] || '',
            })}
            fullWidth
            label="Verification code"
            placeholder="Enter verification code"
            autoFocus
            data-hj-suppress
            error={this.getErrorState()}
            onChange={({ target: { value } }) => this.onFieldChange(value, CODE_FIELD_NAME)}
            onBlur={() => this.onBlur(CODE_FIELD_NAME)}
            onFocus={this.onFocus}
            helperText={this.getOtpCodeFieldHelperText()}
            inputProps={{
              pattern: '[0-9]*',
              maxLength: '4',
              'data-testid': 'otpCode',
            }}
            type="tel"
          />
          {this.props.requiresTosConsent && (
            <TosMessage>
              <TosLinks />
              <input
                {...form.getFieldProps(TOS_ACCEPTED_FIELD_NAME, {
                  initialValue: true,
                })}
                type="hidden"
              />
            </TosMessage>
          )}
          <PersistedLoginInput form={form} />
          <CTA
            text={this.props.requiresTosConsent ? 'Continue & Agree' : 'Verify & Log In'}
            variation={ButtonVariations.pinkPrimary}
            type="submit"
            width="100%"
            disabled={this.isSendCodeButtonDisabled()}
            data-testid="otpCodeFormSubmit"
          />
        </Form>
      </Fragment>
    );
  }
}

OtpCodeLoginForm.propTypes = {
  form: formShape,
  handleOtpFormSubmit: PropTypes.func.isRequired,
  onLoginSuccess: PropTypes.func.isRequired,
  isBookingWidgetEmbedded: PropTypes.bool,
  redirectToUrl: PropTypes.string,
  login: PropTypes.object,
  preventRedirectAfterLogin: PropTypes.bool,
  accountSummary: PropTypes.object,
  initialValues: PropTypes.object,
  query: PropTypes.object,
  requiresTosConsent: PropTypes.bool,
  cookies: instanceOf(Cookies).isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  goToSignUp: PropTypes.func.isRequired,
  handleFormSubmit: PropTypes.func.isRequired,
};

OtpCodeLoginForm.defaultProps = {
  initialValues: {},
};

export default createForm()(withCookies(withAuthentication(OtpCodeLoginForm)));
